import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/account/ChangePasswordDialog";
import {
  LayoutDashboard, Users, Upload, GitMerge, FileSpreadsheet, History,
  UserPlus, ScrollText, Settings, LogOut, Menu, X, FileText, Mail,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/import", label: "Import Orders", icon: Upload, admin: true },
  { to: "/duplicates", label: "Review Duplicates", icon: GitMerge },
  { to: "/imports", label: "Import History", icon: History },
  { to: "/reports", label: "Reports & Export", icon: FileSpreadsheet },
  { to: "/statements", label: "Statements", icon: FileText, admin: true },
  { to: "/email", label: "Send an Email", icon: Mail, admin: true },
  { to: "/team", label: "Team", icon: UserPlus, admin: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, admin: true },
  { to: "/settings", label: "Settings", icon: Settings, admin: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => !n.admin || user?.role === "admin");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const renderNav = (prefix) => (
    <nav className="flex-1 px-4 space-y-1 overflow-y-auto" data-testid={`${prefix}sidebar-nav`}>
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={() => setOpen(false)}
          data-testid={`${prefix}nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200 ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  const userBlock = (
    <div className="px-4 py-5 border-t border-border">
      <p className="text-sm font-semibold text-foreground truncate" data-testid="current-user-name">{user?.name}</p>
      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
      <p className="text-xs text-primary mt-0.5 capitalize">{user?.role === "admin" ? "Administrator" : "Staff"}</p>
      <ChangePasswordDialog />
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="mt-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors duration-200"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );

  const brand = (
    <div className="px-6 py-7">
      <h1 className="text-2xl font-serif font-semibold leading-tight text-foreground">Joanna Beck</h1>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">Ministries</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-[#F5F1E7] border-b border-border sticky top-0 z-40">
        <div>
          <span className="font-serif text-lg font-semibold">Joanna Beck</span>
          <span className="text-xs text-muted-foreground ml-2 uppercase tracking-widest">Ministries</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="p-2">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-30 bg-[#F5F1E7] pt-16 flex flex-col">
          {renderNav("mobile-")}
          {userBlock}
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#F5F1E7] border-r border-border z-30">
        {brand}
        {renderNav("")}
        {userBlock}
      </aside>

      <main className="md:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
